import { ContentType } from '@/@types/common'
import type { Post } from '@/@types/post'
import MixedAttachmentPicker from '@/components/Attachment/pickers/MixedAttachmentPicker'
import { checkAttachmentDisableStatus, filterFiles } from '@/components/Attachment/utils'
import Avatar from '@/components/Avatar'
import type { LexicalEditorHandle } from '@/components/Editor/LexicalEditor'
import { CREATE_POST, EDIT_POST, EDIT_REPOST } from '@/constants/api'
import { Attachment, AttachmentType } from '@/states/attachment'
import { cachePost, retrievePost } from '@/utils/fetchers/post'
import { useCurrentUser } from '@/utils/hooks/useUser'
import { post } from '@/utils/request'
import { css } from '@emotion/react'
import { ErrorMessage } from '@hookform/error-message'
import { zodResolver } from '@hookform/resolvers/zod'
import { MIME_TYPES } from '@mantine/dropzone'
import { first, flatMap, merge } from 'lodash-es'
import { motion } from 'motion/react'
import { type HTMLAttributes, useCallback, useMemo, useRef } from 'react'
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import AttachmentDropzone, { AttachmentDropzoneProps } from '../../Attachment/AttachmentDropzone'
import AttachmentsPreview from '../../Attachment/AttachmentsPreview'
import AttachmentsUploadService from '../../Attachment/AttachmentsUploadService'
import AutoSaveService from '../AutoSaveService'
import ContentEditor from '../ContentEditor'
import RecoverDraftButton from '../RecoverDraftButton'
import SubmitButton from './SubmitButton'
import TopicPicker from './TopicPicker'
import { postFormSchema, type PostFormValue } from './types'
import { buildCreatePayload, buildUpdatePayload } from './utils'

export interface PostFormProps
	extends Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit' | 'defaultValue'> {
	onSubmit?: (data: Post) => void
	defaultValue?: PostFormValue
}

export default function PostForm({ onSubmit, defaultValue, ...props }: PostFormProps) {
	const contentEditorRef = useRef<{ editor: LexicalEditorHandle }>(null)
	const user = useCurrentUser()

	const methods = useForm<PostFormValue>({
		defaultValues: useMemo(
			() =>
				merge(
					{},
					{
						attachments: [],
						content: '',
					},
					defaultValue
				),
			[defaultValue]
		),
		mode: 'onChange',
		resolver: zodResolver(postFormSchema),
	})
	const { isSubmitting, errors } = methods.formState

	const {
		fields: attachments,
		append: addAttachment,
		remove: removeAttachments,
		update: updateAttachment,
	} = useFieldArray<PostFormValue, 'attachments', 'id'>({
		name: 'attachments',
		control: methods.control,
	})
	const attachmentDisableStatus = checkAttachmentDisableStatus(attachments)
	const isPicturePickerDisabled = attachmentDisableStatus[AttachmentType.PICTURE]
	const isVideoPickerDisabled = attachmentDisableStatus[AttachmentType.VIDEO]
	const isLinkPickerDisabled = attachmentDisableStatus[AttachmentType.LINK]

	const handleSubmit = useCallback(
		async (formValue: PostFormValue) => {
			const isCreateMode = !formValue.id
			let result: Post | null = null

			if (isCreateMode) {
				const payload = buildCreatePayload(formValue)
				result = await post(CREATE_POST, payload).then((res) => res.data.data as Post)
			} else if (formValue.id) {
				// update
				const data = retrievePost(formValue.id)
				const payload = buildUpdatePayload(formValue)
				if (!payload || !data) {
					return
				}

				result = await post(data.type === ContentType.POST ? EDIT_POST : EDIT_REPOST, payload).then(
					(res) => res.data.data as Post
				)
			}

			if (!result) {
				throw new Error('Invalid form value')
			}

			methods.reset()
			contentEditorRef.current?.editor.clear()
			onSubmit?.(result)
			cachePost(result)
		},
		[methods, onSubmit]
	)

	const handleAttachmentAdd = async (newAttachments: Attachment[]) => {
		const { success, error } = await postFormSchema.shape.attachments.safeParseAsync([
			...attachments,
			...newAttachments,
		])
		if (!success) {
			const firstError = first(error.errors)?.message
			if (firstError) {
				toast.error(firstError)
				toast('请在即刻App中重试')
			}

			return
		}
		addAttachment(newAttachments)
	}
	const handleAttachmentsDropped: AttachmentDropzoneProps['onDrop'] = async (files: File[]) => {
		const attachmentGroup = filterFiles(files)
		const newAttachments = flatMap(attachmentGroup)

		await handleAttachmentAdd(newAttachments)
	}

	return (
		<FormProvider {...methods}>
			<AttachmentDropzone
				onDrop={handleAttachmentsDropped}
				accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.mp4]}
			>
				<div
					css={css`
						display: grid;
						grid-template-columns: auto 1fr;
						gap: 10px;
						align-self: stretch;
						background: var(--bg-body-1, #fff);
					`}
				>
					<Avatar
						data={user}
						css={css`
							margin: 4px 0;
						`}
					/>
					<form
						onSubmit={methods.handleSubmit(handleSubmit)}
						css={css`
							padding-top: 10px;
							overflow: hidden;
						`}
						{...props}
					>
						<Controller
							name="content"
							render={({ field }) => <ContentEditor {...field} ref={contentEditorRef} />}
						/>

						<section
							css={css`
								margin-top: 18px;
								overflow: hidden;
								height: 26px;
							`}
						>
							<Controller name="topic" render={({ field }) => <TopicPicker {...field} />} />
						</section>

						{/* attachment fields */}
						<footer
							css={css`
								margin-top: 12px;
								display: flex;
								align-items: center;
							`}
						>
							<MixedAttachmentPicker
								isLinkPickerDisabled={isLinkPickerDisabled}
								isPicturePickerDisabled={isPicturePickerDisabled}
								isVideoPickerDisabled={isVideoPickerDisabled}
								onAdd={handleAttachmentAdd}
							/>

							<RecoverDraftButton
								draftType={ContentType.POST}
								onRecover={(nextData) => {
									const draft = nextData as PostFormValue
									for (const key in draft) {
										methods.setValue(
											key as keyof PostFormValue,
											draft[key as keyof PostFormValue],
											{
												shouldDirty: false,
											}
										)
									}
									contentEditorRef.current?.editor.setText(nextData.content)
								}}
								css={css`
									margin-left: 20px;
								`}
							/>

							<SubmitButton
								loading={isSubmitting}
								css={css`
									margin-left: auto;
								`}
							/>
						</footer>
						<AttachmentsPreview
							data={attachments}
							onDelete={(id) => {
								const index = attachments.findIndex((item) => item.id === id)
								index !== -1 && removeAttachments(index)
							}}
						/>
						<ErrorMessage
							name="attachments"
							errors={errors}
							render={({ message }) => (
								<motion.p
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									css={css`
										font-size: 14px;
										font-weight: 500;
										color: var(--tint-error);
										margin-top: 12px;
										margin-bottom: unset;
									`}
								>
									{message}
								</motion.p>
							)}
						/>
						{!errors.attachments && (
							<AttachmentsUploadService data={attachments} onUpdate={updateAttachment} />
						)}
						<AutoSaveService draftType={ContentType.POST} />
					</form>
				</div>
			</AttachmentDropzone>
		</FormProvider>
	)
}
